<template>
  <component :is="wrapperTag" :class="wrapperClass">
    <!-- Frozen footer row -->
    <template v-if="row._isFrozenFooter">
      <strong v-if="column.footerRenderer">
        <div v-html="renderFooterCellSanitizado()"></div>
      </strong>
      <strong v-else-if="column.data && column.footerType && column.footerType !== 'none' && row[column.data] !== undefined">
        <span v-if="column.renderType === 'currency' || column.footerCurrency">
          {{ n(parseFloat(String(row[column.data])) || 0, 'currency') }}
        </span>
        <span v-else-if="column.renderType === 'number'">
          {{ n(parseFloat(String(row[column.data])) || 0, 'decimal') }}
        </span>
        <span v-else-if="column.renderType === 'integer'">
          {{ n(parseFloat(String(row[column.data])) || 0, 'integer') }}
        </span>
        <span v-else-if="column.renderType === 'percentage'">
          {{ n((parseFloat(String(row[column.data])) || 0) / 100, 'percent') }}
        </span>
        <span v-else>
          {{ row[column.data] }}
        </span>
      </strong>
    </template>

    <!-- Regular cell rendering -->
    <template v-else>
      <!-- Handle custom cellRenderer (returns HTML element or string) -->
      <div v-if="column.cellRenderer" class="dt-cell-html" v-html="renderCustomCellSanitizado()"></div>

      <!-- Handle built-in renderTypes -->
      <template v-else-if="column.renderType">
        <!-- Currency -->
        <span v-if="column.renderType === 'currency' && column.data">
          {{ n(parseFloat(String(row[column.data])) || 0, 'currency') }}
        </span>

        <!-- Number -->
        <span v-else-if="column.renderType === 'number' && column.data">
          {{ n(parseFloat(String(row[column.data])) || 0, 'decimal') }}
        </span>

        <!-- Integer -->
        <span v-else-if="column.renderType === 'integer' && column.data">
          {{ n(parseFloat(String(row[column.data])) || 0, 'integer') }}
        </span>

        <!-- Percentage -->
        <span v-else-if="column.renderType === 'percentage' && column.data">
          {{ n((parseFloat(String(row[column.data])) || 0) / 100, 'percent') }}
        </span>

        <!-- Date -->
        <span v-else-if="column.renderType === 'date' && column.data">
          {{ getDisplayValue(row, column.data, deps, 'date') }}
        </span>

        <!-- DateTime -->
        <span v-else-if="column.renderType === 'datetime' && column.data">
          {{ getDisplayValue(row, column.data, deps, 'datetime') }}
        </span>

        <!-- Yes/No -->
        <span v-else-if="column.renderType === 'yes_no' && column.data">
          {{ row[column.data] ? t('Sim') : t('Não') }}
        </span>

        <!-- Active/Inactive -->
        <span v-else-if="column.renderType === 'active_inactive' && column.data">
          {{ row[column.data] ? t('Ativo') : t('Inativo') }}
        </span>

        <!-- Clickable ID -->
        <a
          v-else-if="column.renderType === 'clickable_id' && column.data"
          :data-testid="cellTestIdLocal('edit')"
          href="javascript:void(0)"
          class="clickable-id"
          @click.prevent.stop="emit('edit-click', row[column.data])">
          {{ row[column.data] }}
          <i class="pi pi-pencil"></i>
        </a>

        <!-- Clickable Button -->
        <template v-else-if="column.renderType === 'clickable_button'">
          <Button
            v-if="!column.buttonCondition || column.buttonCondition(row)"
            :label="column.buttonText || 'Action'"
            :icon="column.buttonIcon"
            :class="column.buttonClass"
            :data-testid="resolveButtonTestIdLocal()"
            :disabled="typeof column.buttonDisabled === 'function' ? column.buttonDisabled(row) : !!column.buttonDisabled"
            :title="typeof column.buttonTooltip === 'function' ? column.buttonTooltip(row) : column.buttonTooltip"
            size="small"
            @click.stop="emit('button-click', { value: column.data ? row[column.data] : null, data: row, action: column.buttonAction })"
          />
          <span v-else></span>
        </template>

        <!-- Action Buttons -->
        <template v-else-if="column.renderType === 'action_buttons' && column.buttons">
          <div class="action-buttons-cell">
            <template v-for="(btn, btnIndex) in column.buttons" :key="btnIndex">
              <Button
                v-if="!btn.condition || btn.condition(row)"
                :data-testid="resolveActionBtnTestId(btn, btnIndex)"
                :label="btn.text"
                :icon="btn.icon"
                :class="btn.class"
                :title="typeof btn.tooltip === 'function' ? btn.tooltip(row) : btn.tooltip"
                :disabled="typeof btn.disabled === 'function' ? btn.disabled(row) : !!btn.disabled"
                size="small"
                @click.stop="emit('button-click', { value: null, data: row, action: btn.action })"
              />
            </template>
          </div>
        </template>

        <!-- Copy on Click -->
        <template v-else-if="column.renderType === 'copy_on_click' && column.data">
          <a
            v-if="row[column.data] !== null && row[column.data] !== undefined && row[column.data] !== ''"
            href="javascript:void(0)"
            class="copy-on-click"
            :data-testid="`${column.data}-copy`"
            :title="t('Clique para copiar')"
            @click.prevent="handleCopyClick(column, row[column.data], row)">
            {{ getDisplayValue(row, column.data, deps) }}
            <i class="pi pi-copy"></i>
          </a>
          <span v-else></span>
        </template>

        <!-- Custom Clickable ID -->
        <template v-else-if="column.renderType === 'custom_clickable_id' && column.data">
          <a
            v-if="row[column.data]"
            :data-testid="cellTestIdLocal('custom')"
            href="javascript:void(0)"
            class="clickable-id"
            @click.prevent.stop="handleCustomClick(column, row[column.data], row)">
            {{ row[column.data] }}
            <i class="pi pi-pencil"></i>
          </a>
          <span v-else></span>
        </template>

        <!-- Checkbox -->
        <input
          v-else-if="column.renderType === 'checkbox'"
          :data-testid="cellTestIdLocal('checkbox')"
          type="checkbox"
          :class="(column.checkboxClass as string) || 'data-checkbox'"
          :checked="column.checkboxIsChecked ? column.checkboxIsChecked(row) : false"
          @change="handleCheckboxChange(column, row, $event)"
        />

        <!-- Toggle Switch -->
        <ToggleSwitch
          v-else-if="column.renderType === 'toggle' && column.data"
          :modelValue="(row[column.data] as boolean | string | undefined)"
          @update:modelValue="emit('toggle-change', { column, rowData: row, newValue: $event })"
          @click.stop
        />

        <!-- Badge -->
        <Badge
          v-else-if="column.renderType === 'badge' && column.badgeConfig"
          :value="column.badgeConfig(row).label"
          :severity="column.badgeConfig(row).severity"
          :title="column.badgeConfig(row).tooltip"
        />

        <!-- KPI Progress Bar -->
        <div v-else-if="column.renderType === 'kpi_progress' && column.data" class="kpi-progress-cell">
          <span class="kpi-progress-label">{{ row[column.data] }}%</span>
          <KpiProgressBar
            :value="parseFloat(String(row[column.data])) || 0"
            :success-threshold="(column.successThreshold as number) ?? 100"
            :warning-threshold="(column.warningThreshold as number) ?? 80"
          />
        </div>

        <!-- Image renderType -->
        <DataTableImageCell
          v-else-if="column.renderType === 'image'"
          :column="column" :row="row" :row-index="rowIndex"
          :test-id-prefix="effectivePrefix" :dense="isSubCell" />

        <!-- Default for unknown renderType -->
        <span v-else>{{ column.data ? getDisplayValue(row, column.data, deps) : '' }}</span>
      </template>

      <!-- No renderType or cellRenderer -->
      <span v-else>{{ column.data ? getDisplayValue(row, column.data, deps) : '' }}</span>
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import Badge from 'primevue/badge';
import { eventBus } from '../eventBus';
import { sanitizeHtmlContent } from '../utils/sanitizeHtmlContent';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from '../composables/useDateFormatter';
import { getDisplayValue, renderSubValue } from './cellRenderers';
import type { CellRenderDeps } from './cellRenderers';
import type { DataTableColumn } from './types';
import DataTableImageCell from './DataTableImageCell.vue';

const props = defineProps<{
  column: DataTableColumn;
  row: Record<string, unknown>;
  isSubCell?: boolean;
  testIdPrefix?: string;
  rowIndex?: number;
}>();

const emit = defineEmits<{
  (e: 'edit-click', value: unknown): void;
  (e: 'button-click', payload: { value: unknown; data: Record<string, unknown>; action: unknown }): void;
  (e: 'checkbox-change', payload: { rowData: Record<string, unknown>; checked: boolean }): void;
  (e: 'toggle-change', payload: { column: DataTableColumn; rowData: Record<string, unknown>; newValue: boolean }): void;
}>();

const { t, n } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

const deps: CellRenderDeps = { n, formatDate, formatDateTime };

// Sub-cells of a merged column render as <small class="cell-subline dt-merged-subline">
// so legacy CSS selectors (.cell-subline) and tests targeting the historical structural
// element continue to work alongside the newer .dt-merged-subline class.
const wrapperTag = computed<string>(() => (props.isSubCell ? 'small' : 'div'));
const wrapperClass = computed(() => (props.isSubCell ? 'cell-subline dt-merged-subline' : ''));

const effectivePrefix = computed(() => {
  const explicit = props.testIdPrefix?.trim();
  return explicit || 'datatable';
});

function cellTestIdLocal(suffix?: string): string {
  const colKey = (props.column && props.column.data) || 'col';
  const idx = props.rowIndex ?? 0;
  return `${effectivePrefix.value}-row-${idx}-${colKey}${suffix ? `-${suffix}` : ''}`;
}

function resolveActionBtnTestId(btn: Record<string, unknown>, btnIndex: number): string {
  const tid = btn.testId;
  if (typeof tid === 'function') {
    return String(tid(props.row));
  }
  return String(tid || cellTestIdLocal(`action-${btnIndex}`));
}

function resolveButtonTestIdLocal(): string {
  const col = props.column;
  const btn = col.buttonTestId;
  if (typeof btn === 'function') {
    return String(btn(props.row));
  }
  return String(btn || cellTestIdLocal('button'));
}

/**
 * Tudo que sai daqui vai para um `v-html` (linha 30). Passa pelo sanitizador —
 * mesma decisão e mesmas razões do `renderCustomCellSanitizado` do `DataTable.vue`; este
 * é o gêmeo dele, para o modo de célula avulsa.
 *
 * O ramo SEM `cellRenderer` também é sanitizado: `props.row[col.data]` é valor
 * cru do banco indo direto para `v-html`.
 */
function renderCustomCellSanitizado(): unknown {
  const col = props.column;
  if (!col.cellRenderer || typeof col.cellRenderer !== 'function') {
    return sanitizeHtmlContent(String(col.data ? props.row[col.data] ?? '' : ''));
  }
  try {
    const result = col.cellRenderer({ value: col.data ? props.row[col.data] : null, data: props.row, node: {} });
    if (result instanceof HTMLElement) return sanitizeHtmlContent(result.outerHTML);
    return sanitizeHtmlContent(String(result ?? ''));
  } catch (error) {
    log('DataTableCell', 'renderCustomCellSanitizado', 'Error', error);
    return sanitizeHtmlContent(String(col.data ? props.row[col.data] ?? '' : ''));
  }
}

/** O rodapé congelado tem `v-html` próprio, no template — mesma limpeza. */
function renderFooterCellSanitizado(): string {
  const col = props.column;
  if (!col.footerRenderer || typeof col.footerRenderer !== 'function') return '';

  return sanitizeHtmlContent(
    String(col.footerRenderer({ value: col.data ? props.row[col.data] : null, data: props.row }) ?? ''),
  );
}

function handleCustomClick(column: DataTableColumn, value: unknown, data: Record<string, unknown>): void {
  if (column.clickCallback && typeof column.clickCallback === 'function') {
    column.clickCallback(value, data);
  } else {
    emit('edit-click', value);
  }
}

async function handleCopyClick(column: DataTableColumn, value: unknown, rowData: Record<string, unknown>): Promise<void> {
  const text = typeof column.copyFormatter === 'function'
    ? column.copyFormatter(value, rowData)
    : String(value ?? '');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    eventBus.emit('globalMessage.success', column.copyMessage || t('Copiado para a área de transferência'));
    if (column.copyCallback && typeof column.copyCallback === 'function') {
      column.copyCallback(value, rowData);
    }
  } catch (err: any) {
    eventBus.emit('globalMessage.error', t('Erro ao copiar: ') + (err?.message || ''));
  }
}

function handleCheckboxChange(column: DataTableColumn, rowData: Record<string, unknown>, event: Event): void {
  if (column.checkboxCallback && typeof column.checkboxCallback === 'function') {
    column.checkboxCallback(rowData, event);
  } else {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    emit('checkbox-change', { rowData, checked });
  }
}
</script>
